import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { getContext } from '@fiverr-private/fiverr_context';
import { translate } from '@fiverr-private/i18n-react';
import { Button } from '@fiverr-private/fit';
import { Button as ButtonPenta, IconButton } from '@fiverr-private/button';
import { SearchIcon, ArrowRightIcon } from '@fiverr-private/icons';
import { SearchIcon as SearchIconPenta } from '@fiverr-private/visuals';
import * as SearchSelector from '../../selectors/Search';
import { isTheme } from '../utils';
import { THEME, SUBMIT_BUTTON_TYPE, SEARCH_BAR_SIZE } from '../../utils/constants';
import {
    PILL_SUBMIT_BUTTON_HEIGHT,
    PILL_SUBMIT_BUTTON_SIZE,
    PILL_SM_SUBMIT_BUTTON_SIZE,
    pillSubmitButtonClass,
} from './pillStyles.ve.css';
import { ROUNDED_PILL_SUBMIT_BUTTON_SIZE, roundedPillSubmitButtonClass } from './roundedPillStyles.ve.css';

import './style.scss';

const SubmitButton = ({
    className,
    searchTitle = 'search_bar.search',
    theme,
    type,
    submitButtonText,
    iconOnly,
    size,
}) => {
    const { locale } = getContext();
    const isButtonInside = type === SUBMIT_BUTTON_TYPE.BUTTON_INSIDE;

    /*
     * We need to set theme to DARK_GREEN if the theme is DEFAULT and the button has type BUTTON_INSIDE
     * in order to don't break existing usages.
     */
    const isDarkGreenTheme = isTheme(theme, THEME.DARK_GREEN) || (isTheme(theme, THEME.DEFAULT) && isButtonInside);

    const getButtonText = (fallbackKey) => submitButtonText || translate(fallbackKey, { locale });

    const ThemeButton = () => {
        if (type === SUBMIT_BUTTON_TYPE.TEXT) {
            return getButtonText(searchTitle);
        }

        return (
            <div className="submit-button-icon">
                <SearchIcon />
            </div>
        );
    };

    if (type === SUBMIT_BUTTON_TYPE.PILL) {
        if (iconOnly) {
            /* `submitButtonText` may be a node, which cannot serve as an accessible name. */
            const ariaLabel =
                typeof submitButtonText === 'string' ? submitButtonText : translate('search_bar.explore_marketplace');
            const isSmall = size === SEARCH_BAR_SIZE.SM;
            const buttonSize = isSmall ? PILL_SM_SUBMIT_BUTTON_SIZE : PILL_SUBMIT_BUTTON_SIZE;

            return (
                <IconButton
                    aria-label={ariaLabel}
                    intent="primary"
                    shape="circle"
                    flexShrink="0"
                    width={buttonSize}
                    height={buttonSize}
                >
                    <SearchIconPenta size={isSmall ? 'sm' : undefined} />
                </IconButton>
            );
        }

        return (
            <ButtonPenta
                className={pillSubmitButtonClass}
                borderRadius="circle"
                flexShrink="0"
                fontWeight="bold"
                height={PILL_SUBMIT_BUTTON_HEIGHT}
            >
                {getButtonText('search_bar.explore_marketplace')}
            </ButtonPenta>
        );
    }

    if (type === SUBMIT_BUTTON_TYPE.ROUNDED_PILL) {
        /* `submitButtonText` may be a node, which cannot serve as an accessible name. */
        const ariaLabel =
            typeof submitButtonText === 'string' ? submitButtonText : translate('search_bar.explore_marketplace');

        return (
            <IconButton
                aria-label={ariaLabel}
                intent="primary"
                className={roundedPillSubmitButtonClass}
                flexShrink="0"
                width={ROUNDED_PILL_SUBMIT_BUTTON_SIZE}
                height={ROUNDED_PILL_SUBMIT_BUTTON_SIZE}
            >
                <SearchIconPenta size="sm" />
            </IconButton>
        );
    }

    if (type === SUBMIT_BUTTON_TYPE.TEXT_WITH_ARROW) {
        return (
            <ButtonPenta intent="primary" variant="filled">
                {getButtonText('search_bar.search_freelancers')}
                <ArrowRightIcon color="white" />
            </ButtonPenta>
        );
    }

    return (
        <Button
            className={classnames(
                'submit-button',
                { 'flex-center': type === SUBMIT_BUTTON_TYPE.BUTTON_INSIDE },
                { 'dark-search-button': isTheme(theme, THEME.DARK) },
                { 'dark-green-search-button': isDarkGreenTheme },
                className
            )}
        >
            <ThemeButton />
        </Button>
    );
};

SubmitButton.propTypes = {
    className: PropTypes.string,
    searchTitle: PropTypes.string,
    theme: PropTypes.string,
    type: PropTypes.string,
    submitButtonText: PropTypes.node,
    iconOnly: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(SEARCH_BAR_SIZE)),
};

SubmitButton.defaultProps = {
    theme: 'default',
    size: SEARCH_BAR_SIZE.MD,
};

const mapStateToProps = (state) => ({
    searchTitle: SearchSelector.searchTitle(state),
    rollouts: state.rollouts,
});

export { SubmitButton };

export default connect(mapStateToProps)(SubmitButton);
